/**
 * Shared behavior for the unified salon-product-card snippet:
 *  - Wishlist: persists saved product ids in localStorage and keeps every
 *    heart button on the page (including ones injected later, e.g. by the
 *    recently-viewed Section Rendering API fetch) in sync.
 *  - Add to bag: intercepts the card's add-to-cart form and submits it via
 *    the theme's native AJAX cart flow (same CartLinesUpdateEvent used by
 *    the theme's own <product-form>), so the cart icon/drawer update live
 *    instead of reloading the page.
 *
 * This file is loaded with `type="module"` from multiple places (the header,
 * and every snippet/section that renders a product card) so it is always
 * available wherever a wishlist heart or add-to-bag button can appear.
 * Different liquid sections render in isolated scopes (the header section
 * and the main page section can't share a liquid variable to dedupe the
 * <script> tag between them), so more than one copy of this tag can land on
 * the same page - and in practice each copy's top-level code DOES run
 * independently. Every side effect below is therefore guarded by
 * `window.__salonProductCardInitialized` so registering the wishlist click
 * handler (and the other listeners) twice doesn't toggle the wishlist
 * state twice per click - previously this silently undid every click,
 * because the second, duplicate listener immediately re-added whatever the
 * first one had just removed.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const WISHLIST_STORAGE_KEY = 'salon:wishlist';

/* =========================================
   WISHLIST
========================================= */

function readWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (error) {
    return [];
  }
}

function writeWishlist(ids) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    /* localStorage unavailable (private mode, quota, etc.) - fail silently */
  }

  document.dispatchEvent(new CustomEvent('salon:wishlist:update', { detail: { ids } }));
}

/* Wishlist entries are keyed by product HANDLE, not numeric id - the only
   public, unauthenticated endpoint for looking a product back up (to build
   its card on the wishlist page) is `/products/<handle>.js`, which needs a
   handle. This also matches how `@theme/recently-viewed-products` already
   stores its own history, for the same reason. */

export function getWishlistHandles() {
  return readWishlist();
}

export function isWishlisted(handle) {
  return readWishlist().includes(String(handle));
}

export function toggleWishlist(handle) {
  const value = String(handle);
  const handles = readWishlist();
  const index = handles.indexOf(value);

  if (index > -1) {
    handles.splice(index, 1);
  } else {
    handles.push(value);
  }

  writeWishlist(handles);

  return handles.includes(value);
}

function syncWishlistButtons(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const handles = readWishlist();

  scope.querySelectorAll('[data-salon-wishlist]').forEach((button) => {
    const handle = button.getAttribute('data-product-handle');
    const saved = handle != null && handles.includes(String(handle));

    button.classList.toggle('is-active', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  });

  document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
    // Only touch textContent when it actually needs to change. Setting
    // textContent always replaces child nodes (even to an identical value),
    // which is a childList mutation - and this page is also watched by a
    // MutationObserver (below) that re-runs this very function whenever
    // nodes are added. Without this guard, every call would requeue
    // another mutation forever and freeze the tab.
    const nextCount = handles.length > 0 ? String(handles.length) : '';
    if (element.textContent !== nextCount) {
      element.textContent = nextCount;
    }
    element.classList.toggle('hidden', handles.length === 0);
  });

  document.querySelectorAll('[data-wishlist-link]').forEach((element) => {
    element.classList.toggle('has-items', handles.length > 0);
  });
}

function handleWishlistClick(event) {
  const button = event.target.closest?.('[data-salon-wishlist]');
  if (!button) return;

  const handle = button.getAttribute('data-product-handle');
  if (!handle) return;

  const saved = toggleWishlist(handle);

  button.classList.toggle('is-active', saved);
  button.setAttribute('aria-pressed', saved ? 'true' : 'false');

  button.classList.add('is-animating');
  window.setTimeout(() => button.classList.remove('is-animating'), 220);
}

/* =========================================
   ADD TO BAG
========================================= */

const ADDED_STATE_DURATION = 1600;

function getCartSectionIds() {
  const ids = [];

  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      ids.push(element.dataset.sectionId);
    }
  });

  return ids;
}

async function fetchCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

  return response.json();
}

async function handleAddToCartSubmit(event) {
  const form = event.target.closest?.('[data-salon-add-to-cart-form]');
  if (!form) return;

  event.preventDefault();

  const button = form.querySelector('[data-salon-add-to-cart-button]');
  const label = form.querySelector('[data-salon-add-to-cart-label]');
  const originalLabel = label ? label.textContent : null;

  if (button?.disabled || button?.classList.contains('is-loading')) return;

  if (!form.reportValidity()) return;
  const errorBox = form.querySelector('[data-salon-cart-error]');
  if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = Number(formData.get('quantity'));
  if (!Number.isInteger(quantity) || quantity < 1) return;
  const productId = form.getAttribute('data-product-id');

  const sectionIds = getCartSectionIds();
  if (sectionIds.length > 0) {
    formData.append('sections', sectionIds.join(','));
  }

  button?.classList.add('is-loading');

  const deferred = CartLinesUpdateEvent.createPromise();
  deferred.promise.catch(() => {});

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const addResponse = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());

    if (addResponse.status) {
      const error = new Error(addResponse.description || addResponse.message || 'Add to cart failed');
      document.dispatchEvent(
        new CartErrorEvent({
          error: addResponse.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: addResponse.description, errors: addResponse.errors },
        })
      );
      deferred.reject(error);
      throw error;
    }

    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'salon-product-card',
        sourceId: productId ?? '',
        itemCount: quantity,
        productId,
        sections: addResponse.sections,
        didError: false,
      },
    });

    button?.classList.remove('is-loading');
    button?.classList.add('is-added');
    if (label) label.textContent = 'Added';

    window.setTimeout(() => {
      button?.classList.remove('is-added');
      if (label && originalLabel != null) label.textContent = originalLabel;
    }, ADDED_STATE_DURATION);
  } catch (error) {
    button?.classList.remove('is-loading');
    if (label && originalLabel != null) label.textContent = originalLabel;
    deferred.reject(error);
    if (errorBox) { errorBox.textContent = error.message || 'Could not add this item. Please try again.'; errorBox.hidden = false; }
    console.error('[salon-product-card] add to cart failed:', error);
  }
}

/* =========================================
   ONE-TIME INITIALIZATION
   Everything above only DEFINES functions (no side effects). Everything
   that actually attaches a listener/observer to the document lives here,
   behind a guard, so it is safe no matter how many times this module's
   top-level code runs on a given page.
========================================= */

if (!window.__salonProductCardInitialized) {
  window.__salonProductCardInitialized = true;

  document.addEventListener('click', handleWishlistClick);
  document.addEventListener('salon:wishlist:update', () => syncWishlistButtons(document));
  document.addEventListener('shopify:section:load', (event) => syncWishlistButtons(event.target));
  document.addEventListener('DOMContentLoaded', () => syncWishlistButtons(document));

  if (document.readyState !== 'loading') {
    syncWishlistButtons(document);
  }

  /* Re-sync whenever new cards are dropped into the page outside of a normal
     section re-render (e.g. the recently-viewed display script injecting
     fetched card markup via innerHTML, which never fires DOMContentLoaded). */
  const wishlistObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        syncWishlistButtons(document);
        return;
      }
    }
  });

  wishlistObserver.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('submit', handleAddToCartSubmit);
}

/* =========================================
   SHARED CARD CLONING
   Used anywhere a card for an arbitrary *other* product needs to be built
   client-side from a single server-rendered template card (recently
   viewed, the wishlist page) - rather than relying on the Section
   Rendering API, which isn't reliably available for an unpublished/
   preview theme. Fetches `/products/<handle>.js` and patches a clone of
   the template with that product's data.
========================================= */

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
  return '$' + (cents / 100).toFixed(2);
}

/**
 * @param {HTMLElement} template - A real, fully-rendered `.salon-product-card` element for ANY product.
 * @param {string} handle - The product handle to build a card for.
 * @returns {Promise<HTMLElement | null>}
 */
const cardRequests = new Map();
export async function buildProductCard(template, handle) {
  try {
    if (!handle || typeof handle !== 'string') return null;
    if (!cardRequests.has(handle)) {
      const request = (async () => {
        const base = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${base}products/${encodeURIComponent(handle)}`, {
          credentials: 'same-origin', headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Product page failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = [...doc.querySelectorAll('.salon-product-card')].find(card =>
          card.dataset.salonProductHandle === handle ||
          card.querySelector('[data-salon-wishlist]')?.dataset.productHandle === handle
        );
        if (!source) throw new Error('Product card helper not found for ' + handle);
        return source;
      })();
      cardRequests.set(handle, request);
      request.catch(() => cardRequests.delete(handle));
    }
    const card = document.importNode(await cardRequests.get(handle), true);
    card.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    const saved = isWishlisted(handle);
    const heart = card.querySelector('[data-salon-wishlist]');
    if (heart) { heart.classList.toggle('is-active', saved); heart.setAttribute('aria-pressed', String(saved)); }
    return card;
  } catch (error) {
    console.warn('[salon-product-card] Could not load product card:', handle, error);
    return null;
  }
}

let optionsSequence = 0;
function closeOptions(card, restoreFocus = false) {
  const panel = card.querySelector('[data-salon-options]');
  const toggle = card.querySelector('[data-salon-options-toggle]');
  if (!panel || !toggle) return;
  panel.hidden = true;
  toggle.setAttribute('aria-expanded', 'false');
  toggle.querySelector('.salon-product-card__add-text').textContent = 'See Options';
  if (restoreFocus) toggle.focus();
}
function handleOptionsClick(event) {
  const toggle = event.target.closest?.('[data-salon-options-toggle]');
  if (toggle) {
    const card = toggle.closest('.salon-product-card');
    const panel = card.querySelector('[data-salon-options]');
    if (!panel) return;
    const opening = panel.hidden;
    document.querySelectorAll('[data-salon-options-toggle][aria-expanded="true"]').forEach(other => {
      if (other !== toggle) closeOptions(other.closest('.salon-product-card'));
    });
    if (!opening) { closeOptions(card); return; }
    if (!panel.id) panel.id = 'salon-options-' + (++optionsSequence);
    toggle.setAttribute('aria-controls', panel.id);
    const cardRect = card.getBoundingClientRect();
    const toggleRect = toggle.getBoundingClientRect();
    const bottom = cardRect.bottom - toggleRect.top + 6;
    panel.style.bottom = bottom + 'px';
    panel.style.maxHeight = Math.max(100, toggleRect.top - cardRect.top - 16) + 'px';
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    toggle.querySelector('.salon-product-card__add-text').textContent = 'Close Options';
    return;
  }
  const stepper = event.target.closest?.('[data-salon-quantity]');
  if (stepper && !stepper.disabled) {
    const input = stepper.closest('form').querySelector('input[name="quantity"]');
    if (stepper.dataset.salonQuantity === 'plus') input.stepUp(); else input.stepDown();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
if (!window.__salonOptionsInitialized) {
  window.__salonOptionsInitialized = true;
  document.addEventListener('click', handleOptionsClick);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-salon-options-toggle][aria-expanded="true"]').forEach(toggle => closeOptions(toggle.closest('.salon-product-card'), true));
    }
  });
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-salon-options-toggle][aria-expanded="true"]').forEach(toggle => closeOptions(toggle.closest('.salon-product-card')));
  });
}
